<template>
  <a
    :href="findEventLink()"
    class="event-card-container"
  >
    <div class="cover-image-container">
      <img v-if="event.cover" :src="event.cover" :alt="$adminT('events.coverAlt', 'event cover')" class="cover-image">
      <a
        v-else-if="!isRegularOrganization"
        :href="findEventInfoLink(event.id, true)"
        class="cover-placeholder"
        @click.stop
      >
        {{ $adminT('events.cover', 'Add Cover') }}
      </a>
      <button v-else type="button" class="cover-placeholder" @click.stop="goToEventInfo">
        {{ $adminT('events.cover', 'Add Cover') }}
      </button>
    </div>
    <div class="event-data-container">
      <div class="event-card-heading">
        <div class="event-date-location">
          <span>{{ workflowLabel(formatDate(event.from, event.to)) }}</span>
          <span v-if="event.location && event.location.city" class="event-card-divider" aria-hidden="true">&#8226;</span>
          <span v-if="event.location && event.location.city">{{ workflowLabel(event.location.city) }}</span>
        </div>
        <div class="event-title">{{ workflowLabel(event.title) }}</div>
      </div>
      <div v-if="event.extraModules && event.extraModules.ticketing" class="event-sales-info">
        <template v-if="salesStatsLoading">
          <div class="event-sales-info__item event-sales-info__item--loading" aria-busy="true" :aria-label="$adminT('events.loadingSales', 'Loading sales statistics')">
            <img src="/assets/event-card/ticket.svg" alt="" class="event-sales-info__icon">
            <vue-skeleton-loader rounded radius="4" width="72px" height="17px" animation="fade" />
          </div>
          <span class="event-sales-info__divider" aria-hidden="true">&#8226;</span>
          <div class="event-sales-info__item event-sales-info__item--loading">
            <img src="/assets/event-card/earnings.svg" alt="" class="event-sales-info__icon">
            <vue-skeleton-loader rounded radius="4" width="56px" height="17px" animation="fade" />
          </div>
        </template>
        <template v-else-if="salesStatsUnavailable">
          <div class="event-sales-info__item">{{ $adminT('events.noSalesData', 'Sales data is unavailable.') }}</div>
        </template>
        <template v-else>
          <div class="event-sales-info__item">
            <img src="/assets/event-card/ticket.svg" alt="" class="event-sales-info__icon">
            <span><strong>{{ workflowLabel(event.totalTicketsSold) }}</strong>/{{ workflowLabel(event.totalNumberOfTickets) }}</span>
          </div>
          <span class="event-sales-info__divider" aria-hidden="true">&#8226;</span>
          <div class="event-sales-info__item">
            <img src="/assets/event-card/earnings.svg" alt="" class="event-sales-info__icon">
            <span>{{ workflowLabel(event.totalEarnings) }} €</span>
          </div>
        </template>
      </div>
    </div>
    <v-menu
      v-if="isRegularOrganization"
      v-model="showActionsMenu"
      class="event-card-actions"
      bottom
      left
      offset-y
      content-class="event-actions-menu"
      @click.native.stop
    >
      <v-btn
        slot="activator"
        flat
        icon
        class="event-card-actions__trigger"
        :aria-label="$adminT('events.actions', 'Event actions')"
        @click.native.prevent
      >
        <v-icon>more_vert</v-icon>
      </v-btn>
      <div class="event-actions-menu__content" role="menu" :aria-label="$adminT('events.actions', 'Event actions')">
        <button type="button" class="event-actions-menu__item" role="menuitem" @click.stop="openDuplicateDialog">
          <img src="/assets/event-actions/duplicate-event.svg" alt="" class="event-actions-menu__icon">
          <span>{{ $adminT('events.duplicate', 'Duplicate event') }}</span>
        </button>
        <button type="button" class="event-actions-menu__item" role="menuitem" @click.stop="goToEventInfo">
          <img src="/assets/event-actions/add-event-cover.svg" alt="" class="event-actions-menu__icon">
          <span>{{ event.cover ? $adminT('events.editCover', 'Edit event cover') : $adminT('events.addCover', 'Add event cover') }}</span>
        </button>
        <button type="button" class="event-actions-menu__item" role="menuitem" @click.stop="goToEventDetails">
          <img src="/assets/event-actions/edit-event-details.svg" alt="" class="event-actions-menu__icon">
          <span>{{ $adminT('events.editDetails', 'Edit event details') }}</span>
        </button>
        <v-tooltip v-if="hasActiveTicketSales" top max-width="240">
          <span slot="activator" class="event-actions-menu__disabled-item">
            <button type="button" class="event-actions-menu__item" role="menuitem" disabled>
              <img src="/assets/event-actions/delete-event.svg" alt="" class="event-actions-menu__icon">
              <span>{{ $adminT('events.delete', 'Delete event') }}</span>
            </button>
          </span>
          <span>{{ salesStatsLoading ? $adminT('events.salesLoading', 'Sales data is still loading.') : salesStatsUnavailable ? $adminT('events.noSalesData', 'Sales data is unavailable.') : $adminT('events.cannotDelete', 'This event cannot be deleted because it has active ticket sales or reservations.') }}</span>
        </v-tooltip>
        <button v-else type="button" class="event-actions-menu__item" role="menuitem" @click.stop="openDeleteDialog">
          <img src="/assets/event-actions/delete-event.svg" alt="" class="event-actions-menu__icon">
          <span>{{ $adminT('events.delete', 'Delete event') }}</span>
        </button>
      </div>
    </v-menu>

    <v-dialog v-if="isRegularOrganization" v-model="showDuplicateDialog" max-width="512" @click.stop>
      <v-card class="event-action-dialog">
        <div class="event-action-dialog__header">
          <div class="event-action-dialog__copy">
            <h2>{{ $adminT('events.duplicateQuestion', 'Do you wish to duplicate event') }}</h2>
            <p>{{ $adminT('events.canUndo', 'This action can be undone.') }}</p>
          </div>
          <button type="button" class="event-action-dialog__close" :aria-label="$adminT('events.close', 'Close')" @click="showDuplicateDialog = false">
            <img src="/assets/event-actions/close.svg" alt="">
          </button>
        </div>
        <div class="event-action-dialog__actions">
          <v-btn flat class="event-action-dialog__button event-action-dialog__button--secondary" @click="showDuplicateDialog = false">
            {{ $adminT('events.cancel', 'Cancel') }}
          </v-btn>
          <v-btn depressed :loading="duplicating" class="event-action-dialog__button event-action-dialog__button--primary" @click="duplicateEvent">
            {{ $adminT('events.duplicate', 'Duplicate event') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-if="isRegularOrganization" v-model="showDuplicateSuccessDialog" max-width="512" @click.stop>
      <v-card class="event-action-dialog">
        <div class="event-action-dialog__header">
          <div class="event-action-dialog__success-copy">
            <img src="/assets/event-actions/success.svg" alt="" class="event-action-dialog__success-icon">
            <div class="event-action-dialog__copy">
              <h2>{{ $adminT('events.duplicated', 'Event duplicated successfully') }}</h2>
              <p>{{ $adminT('events.duplicatedHint', 'Your new event has been created with the same details as the original.') }}</p>
            </div>
          </div>
          <button type="button" class="event-action-dialog__close" :aria-label="$adminT('events.close', 'Close')" @click="showDuplicateSuccessDialog = false">
            <img src="/assets/event-actions/close.svg" alt="">
          </button>
        </div>
        <div class="event-action-dialog__actions">
          <v-btn depressed class="event-action-dialog__button event-action-dialog__button--primary" @click="goToDuplicateDetails">
            {{ $adminT('events.goDetails', 'Go to event details') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-if="isRegularOrganization" v-model="showDeleteDialog" max-width="512" @click.stop>
      <v-card class="event-action-dialog">
        <div class="event-action-dialog__header">
          <div class="event-action-dialog__copy event-action-dialog__copy--spaced">
            <h2 v-text="$adminT('events.deleteQuestion', 'Are you sure you want to delete event {{title}}?', { title: event.title })" />
            <p>{{ $adminT('events.cannotUndo', 'This action cannot be undone.') }}</p>
          </div>
          <button type="button" class="event-action-dialog__close" :aria-label="$adminT('events.close', 'Close')" @click="showDeleteDialog = false">
            <img src="/assets/event-actions/close.svg" alt="">
          </button>
        </div>
        <div class="event-action-dialog__actions">
          <v-btn depressed :loading="deleting" class="event-action-dialog__button event-action-dialog__button--danger" @click="deleteEvent">
            {{ $adminT('events.delete', 'Delete event') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </a>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import moment from 'moment'
import { OrganizationType } from '@/types'
import { DUPLICATE_EVENT, SOFT_DELETE_EVENT } from '@/graphql/event.graphql'
import { EventBus } from '@/plugins/event-bus.js'

export default {
  name: 'event-card',
  mixins: [WorkflowDisplay],
  props: {
    event: Object,
    organizationType: String,
    organizationSlug: String,
    salesStatsLoading: {
      type: Boolean,
      default: false,
    },
    salesStatsUnavailable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showDuplicateDialog: false,
      showDuplicateSuccessDialog: false,
      showDeleteDialog: false,
      showActionsMenu: false,
      duplicatedEventId: null,
      duplicating: false,
      deleting: false,
    }
  },
  computed: {
    isRegularOrganization() {
      return this.organizationType === OrganizationType.REGULAR
    },
    hasActiveTicketSales() {
      return this.salesStatsLoading || this.salesStatsUnavailable || Number(this.event.totalTicketsSold) > 0
    },
  },
  methods: {
    formatDate(startDate, endDate) {
      let formattedDate = moment(startDate).format('DD.MM.YYYY.')

      const isEndDateOnSameDay = moment(endDate).isSame(moment(startDate), 'day')

      if (!isEndDateOnSameDay) {
        formattedDate += ` - ${moment(endDate).format('DD.MM.YYYY.')}`
      }

      return formattedDate
    },
    findEventLink() {
      if (this.organizationType === OrganizationType.THEATER) {
        return `/theater-organizations/${this.organizationSlug}/event/${this.event.id}`
      }

      const prefix = this.organizationType === OrganizationType.SPORT_ORGANIZATION ? 'sport-' : ''

      return `/${prefix}organizations/${this.organizationSlug}/events/${this.event.id}${
        this.event.extraModules.ticketing ? '' : '/accreditations'
      }`
    },
    findEventInfoLink(eventId = this.event.id, scrollToCover = false) {
      if (this.organizationType === OrganizationType.THEATER) {
        return `/theater-organizations/${this.organizationSlug}/event/${eventId}/info${
          scrollToCover ? '#event-cover-field' : ''
        }`
      }

      const prefix = this.organizationType === OrganizationType.SPORT_ORGANIZATION ? 'sport-' : ''

      return `/${prefix}organizations/${this.organizationSlug}/events/${eventId}/info${
        scrollToCover ? '#event-cover-field' : ''
      }`
    },
    goToEventInfo() {
      this.showActionsMenu = false
      this.$router.push(this.findEventInfoLink(this.event.id, true))
    },
    goToEventDetails() {
      this.showActionsMenu = false
      this.$router.push(this.findEventInfoLink())
    },
    openDuplicateDialog() {
      this.showActionsMenu = false
      this.showDuplicateDialog = true
    },
    openDeleteDialog() {
      this.showActionsMenu = false
      this.showDeleteDialog = true
    },
    goToDuplicateDetails() {
      this.showDuplicateSuccessDialog = false
      this.$router.push(this.findEventInfoLink(this.duplicatedEventId))
    },
    mutationErrorMessage() {
      return this.$adminT('events.tryAgain', 'Something went wrong. Please try again.')
    },
    showError(error) {
      EventBus.$emit('toggle-snackbar', { adminWorkflowNotification: true,
        adminEventActionError: true,
        title: this.$adminT('events.actionError', 'Unable to complete action'),
        message: this.mutationErrorMessage(error),
        type: 'error',
      })
    },
    async duplicateEvent() {
      this.duplicating = true
      try {
        const { data } = await this.$apollo.mutate({
          mutation: DUPLICATE_EVENT,
          variables: { eventId: this.event.id },
          refetchQueries: ['getEventsAndQuota', 'getWorkingEvents', 'getOrganizationWorkingEvents'],
        })
        this.duplicatedEventId = data.duplicateEvent.id
        this.showDuplicateDialog = false
        this.showDuplicateSuccessDialog = true
      } catch (error) {
        this.showError(error)
      } finally {
        this.duplicating = false
      }
    },
    async deleteEvent() {
      if (this.hasActiveTicketSales) return

      this.deleting = true
      try {
        await this.$apollo.mutate({
          mutation: SOFT_DELETE_EVENT,
          variables: { eventId: this.event.id },
          refetchQueries: ['getEventsAndQuota', 'getWorkingEvents', 'getOrganizationWorkingEvents'],
        })
        this.showDeleteDialog = false
      } catch (error) {
        this.showError(error)
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.event-card-container {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1rem 0;
  padding: 1rem 0;
  cursor: pointer;
  text-decoration: none;
  overflow: hidden;
  background: #fff;
  border-radius: 0.5rem;
  border: 1px solid #d9d9d9;
}

.event-card-container:hover {
  border-color: #979797;
  box-shadow: 0 4px 16px 0 rgba(51, 51, 51, 0.16);
}

.event-card-container:focus-visible {
  outline: none;
  border-color: #d9d9d9;
  box-shadow: 0 0 0 4px #cee4f2;
}

.cover-image-container {
  display: flex;
  flex: 0 0 151px;
  align-items: center;
  justify-content: center;
  width: 151px;
  height: 66.94px;
  padding: 0 16px;
}

.cover-image {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 0.25rem;
}

.cover-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0 8px;
  background: white;
  color: #0277bd;
  text-align: center;
  text-decoration: none;
  font-family: Raleway;
  font-size: 1rem;
  font-weight: 700;
  line-height: 120%;
  border-radius: 0.25rem;
  border: 2px dashed #cee4f2;
}

.cover-placeholder:hover {
  border-color: #56a4ef;
  background: #f0f6fa;
}

.event-data-container {
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  align-items: flex-start;
  min-width: 300px;
  gap: 16px;
  padding: 0 24px 0 16px;
}

.event-card-heading {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
}

.event-date-location {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-family: 'Source Sans Pro';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  white-space: nowrap;
}

.event-card-divider,
.event-sales-info__divider {
  flex: 0 0 4px;
  width: 4px;
  height: 4px;
  overflow: hidden;
  color: #979797;
  font-size: 18px;
  line-height: 4px;
}

.event-title {
  width: 100%;
  color: #666;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.045rem;
}

.event-sales-info {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  width: 100%;
  white-space: nowrap;

  color: #979797;
  font-family: 'Source Sans Pro';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
}

.event-sales-info__item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.event-sales-info__icon {
  display: block;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
}

.event-sales-info__item span {
  font-weight: 400;
}

.event-sales-info__item strong {
  font-weight: 700;
}

.event-card-actions {
  position: absolute;
  top: 7px;
  right: 7px;
}

.event-card-actions__trigger.v-btn {
  min-width: 32px;
  width: 32px;
  height: 32px;
  margin: 0;
  padding: 4px;
  border-radius: 24px;
  background: #fff;
}

.event-card-actions__trigger.v-btn .v-icon {
  color: #666;
}

@media screen and (max-width: 500px) {
  .event-card-container {
    margin: 0 -1.5rem;
    border-radius: 0;
    border-right: 0;
    border-left: 0;
  }

  .event-data-container {
    min-width: 0;
  }
}
</style>

<style>
.event-actions-menu {
  min-width: 262px;
  margin-top: 4px;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(51, 51, 51, 0.16);
}

.event-actions-menu__content {
  width: 262px;
  padding: 0;
  background: #fff;
}

.event-actions-menu__item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  gap: 12px;
  padding: 12px;
  border: 0;
  background: #fff;
  color: #0277bd;
  cursor: pointer;
  font-family: Raleway, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.015em;
  text-align: left;
}

.event-actions-menu__item:hover,
.event-actions-menu__item:focus-visible {
  background: #f0f6fa;
  outline: 0;
}

.event-actions-menu__item:focus-visible {
  box-shadow: inset 0 0 0 2px #56a4ef;
}

.event-actions-menu__item:disabled {
  color: #979797;
  cursor: not-allowed;
}

.event-actions-menu__item:disabled:hover {
  background: #fff;
}

.event-actions-menu__disabled-item {
  display: block;
}

.event-actions-menu__icon {
  display: block;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
}

.event-action-dialog {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 0 12px rgba(51, 51, 51, 0.2);
}

.event-action-dialog__header {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}

.event-action-dialog__copy {
  flex: 1 1 0;
  min-width: 0;
}

.event-action-dialog__copy--spaced {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.event-action-dialog__copy h2 {
  margin: 0;
  color: #0277bd;
  font-family: Raleway, sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.03em;
}

.event-action-dialog__copy p {
  margin: 4px 0 0;
  color: #666;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
}

.event-action-dialog__copy--spaced p {
  margin-top: 0;
}

.event-action-dialog__close {
  display: block;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.event-action-dialog__close img {
  display: block;
  width: 100%;
  height: 100%;
}

.event-action-dialog__success-copy {
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  gap: 8px;
}

.event-action-dialog__success-icon {
  display: block;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
}

.event-action-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 8px;
}

.event-action-dialog__button.v-btn {
  min-width: 0;
  height: 48px;
  margin: 0;
  padding: 0 24px;
  border-radius: 24px;
  box-shadow: none;
}

.event-action-dialog__button.v-btn .v-btn__content {
  font-family: Raleway, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.015em;
}

.event-action-dialog__button--secondary.v-btn {
  background: transparent;
  color: #0277bd;
}

.theme--light.v-btn.event-action-dialog__button--primary:not(.v-btn--disabled) {
  background-color: #0277bd !important;
  color: #fff !important;
}

.theme--light.v-btn.event-action-dialog__button--danger:not(.v-btn--disabled) {
  background-color: #db384c !important;
  color: #fff !important;
}

@media screen and (max-width: 560px) {
  .event-action-dialog {
    padding: 20px;
  }

  .event-action-dialog__actions {
    flex-wrap: wrap;
  }
}
</style>
